import {Game} from "../../Game";
import {gsap} from "gsap";
import {IFeatureData} from "../../server/IData";
import {Symbol, SymbolStates} from "../Symbol";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {BLEND_MODES} from "pixi.js";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";

export class SymbolFloors extends PIXI.Container {

    private _floors: PIXI.Sprite[][];
    private _floorsContainer: PIXI.Container;
    private _maskItem: PIXI.Graphics;
    private _textureName: string = "floor";
    private _hatAnimation: PIXI.spine.Spine;
    private _hatHurtAnimation: PIXI.spine.Spine;

    constructor() {
        super();
        this.name = "SymbolFloors";

        this.init();
    }

    public doGridExpansion(extraReels: number, extraRows: number, addReelsToRight: boolean, addRowsInBottom: boolean, duration: number): gsap.core.Timeline {
        const prevWidth: number = this._floors[0][0].width,
            prevHeight: number = this._floors[0][0].height,
            xScaleForNewFloor: number = Math.round(((prevWidth) / (Game.DEFAULT_SYMBOL_WIDTH)) * 10) / 10,
            yScaleForNewFloor: number = Math.round(((prevHeight) / (Game.DEFAULT_SYMBOL_HEIGHT)) * 10) / 10,
            textureHelper: string = this._textureName;

        // #130
        if (this._floors.length >= Game.TOTAL_REELS_NUM) {
            addReelsToRight = false;
            extraReels = 0;
        }
        if (this._floors[0].length >= Game.TOTAL_REELS_NUM) {
            addRowsInBottom = false;
            extraRows = 0;
        }

        if (addReelsToRight) {
            for (let reelId: number = Game.ACTIVE_REELS_NUM; reelId < extraReels + Game.ACTIVE_REELS_NUM; reelId++) {
                this._floors.push([]);
                this.increaseRows(0, extraRows, true, reelId, 0, xScaleForNewFloor, yScaleForNewFloor, prevWidth, prevHeight, textureHelper);
            }

            for (let reelId: number = 0; reelId < Game.ACTIVE_REELS_NUM; reelId++) {
                this.increaseRows((addRowsInBottom ? Game.ACTIVE_ROWS_NUM : 0), extraRows, addRowsInBottom, reelId, 0, xScaleForNewFloor, yScaleForNewFloor, prevWidth, prevHeight, textureHelper);
            }
        } else {
            if (extraReels) {
                for (let reelId: number = 0; reelId < extraReels; reelId++) {
                    this._floors.unshift([]);
                }
                for (let reelId: number = extraReels - 1; reelId > -1; reelId--) {
                    this.increaseRows(0, extraRows, true, reelId, -extraReels, xScaleForNewFloor, yScaleForNewFloor, prevWidth, prevHeight, textureHelper);
                }
            }

            for (let reelId: number = extraReels; reelId < Game.ACTIVE_REELS_NUM + extraReels; reelId++) {
                this.increaseRows(Game.ACTIVE_ROWS_NUM, extraRows, addRowsInBottom, reelId, 0, xScaleForNewFloor, yScaleForNewFloor, prevWidth, prevHeight, textureHelper);
            }
        }

        return this.scaleExpandedGrid(duration, extraReels);
    }

    public resetGrid(duration: number, gridSize: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            [xOffset, yOffset]: number[] = this.getReelPositions(gridSize);

        for (let i: 0 = 0; i < Game.ACTIVE_REELS_NUM; i++) {
            for (let j: number = 0; j < Game.ACTIVE_ROWS_NUM; j++) {
                const floor: PIXI.Sprite = this._floors[i][j];

                tl.to(floor, {
                    duration: duration,
                    x: (i * Game.SYMBOL_WIDTH),
                    y: (j * Game.SYMBOL_HEIGHT)
                }, "<");
            }
        }

        tl.to(this._maskItem.scale, {
            duration,
            x: 1,
            y: 1
        }, "<");
        tl.to(this._floorsContainer.pivot, {
            duration,
            x: 330,
            y: 300
        }, "<");
        tl.to(this._floorsContainer.scale, {
            duration,
            x: 1,
            y: 1
        }, "<");
        tl.to(this, {
            duration,
            x: xOffset,
            y: yOffset
        }, "<");

        // left, right small mask
        tl.to([this._maskItem.children[0], this._maskItem.children[2]], {
            duration,
            x: -110
        }, "<");

        tl.add(() => {
            while (this._floors.length > Game.DEFAULT_REELS_NUM) {
                this._floors.pop();
            }
            this._floors.forEach((floors: PIXI.Sprite[]) => {
                while (floors.length > Game.DEFAULT_ROWS_NUM) {
                    floors.pop();
                }
            });

            this._floorsContainer.removeChildren();
            for (let reel: number = 0; reel < this._floors.length; reel++) {
                for (let row: number = 0; row < this._floors[reel].length; row++) {
                    const floor: PIXI.Sprite = this._floors[reel][row];
                    floor.position.set((reel * Game.SYMBOL_WIDTH), (row * Game.SYMBOL_HEIGHT));
                    this._floorsContainer.addChild(floor);
                }
            }
        });

        return tl;
    }

    public scaleExpandedGrid(duration: number, extraReels: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            maskScaleToX: number = (Game.SYMBOL_WIDTH * (this._floors.length - 2)) / (4 * Game.DEFAULT_SYMBOL_WIDTH),
            maskScaleToY: number = (Game.SYMBOL_HEIGHT * (this._floors[0].length - 2)) / (4 * Game.DEFAULT_SYMBOL_HEIGHT);

        for (let i: 0 = 0; i < this._floors.length; i++) {
            for (let j: number = 0; j < this._floors[i].length; j++) {
                const floor: PIXI.Sprite = this._floors[i][j];

                tl.to(floor, {
                    duration,
                    x: (Game.DEFAULT_SYMBOL_WIDTH * i),
                    y: (Game.DEFAULT_SYMBOL_HEIGHT * j),
                    onStart: () => {
                        floor.alpha = 1;
                    }
                }, "<");
            }
        }

        tl.to(this._floorsContainer.pivot, {
            duration,
            x: (this._floors.length * Game.DEFAULT_SYMBOL_WIDTH) / 2,
            y: (this._floors[0].length * Game.DEFAULT_SYMBOL_HEIGHT) / 2
        }, "<");
        tl.to(this._floorsContainer.scale, {
            duration,
            x: Game.scaleFactor,
            y: Game.scaleFactor
        }, "<");
        tl.to(this._maskItem.scale, {
            duration,
            x: maskScaleToX,
            y: maskScaleToY
        }, "<");

        if (extraReels) {
            // left small mask
            tl.to(this._maskItem.children[0], {
                duration,
                x: -109 * Game.scaleFactor
            }, "<");
            // right small mask
            tl.to(this._maskItem.children[2], {
                duration,
                x: -109 - (109 * (1 - Game.scaleFactor))
            }, "<");
        }

        return tl;
    }

    public playExplodePatternHighlightAnimation(reelId: number, rowId: number, duration: number = 5 / 30): gsap.core.Timeline {
        const item: PIXI.Graphics = new PIXI.Graphics(),
            tl: gsap.core.Timeline = gsap.timeline();

        item.beginFill(0xff0000, 0.1).drawRect(0, 0, Game.DEFAULT_SYMBOL_WIDTH, Game.DEFAULT_SYMBOL_HEIGHT).endFill();
        item.alpha = 0;
        item.name = "explosionHighlight";
        this._floors[reelId][rowId].addChildAt(item, 0);

        tl.to(item, {
            alpha: 1,
            duration
        });

        return tl;
    }

    public playExplodePatternSparkAnimation(reelId: number, rowId: number, duration: number = 0): gsap.core.Timeline {
        const item: TimelineSprite = new TimelineSprite(Game.assetLoader.getTextures("single_explos/single_explos_")),
            tl: gsap.core.Timeline = gsap.timeline(),
            floor: PIXI.Sprite = this._floors[reelId][rowId],
            symbol: Symbol = new Symbol("P4", SymbolStates.NORMAL, rowId);

        item.anchor.set(0.5);
        item.blendMode = PIXI.BLEND_MODES.SCREEN;
        item.scale.set(1);
        item.hide();

        symbol.position.set(floor.width / 2, floor.height / 2);
        symbol.addChild(item);
        floor.addChild(symbol);

        tl.to(symbol.animation.scale, {
            duration,
            x: 0,
            y: 0
        });

        tl.add(<gsap.core.Timeline><unknown>item.getAnimationAutoShowHide(true, true, undefined, undefined), undefined);

        tl.add(() => {
            if (item.parent) {
                item.parent.removeChild(item);
                item.destroy();
            }

            if (symbol.parent) {
                symbol.parent.removeChild(symbol);
            }
        });

        return tl;
    }

    public stopExplodePatternHighlightAnimation(duration: number = 5 / 30): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        for (let floorArr of this._floors) {
            for (let floorCell of floorArr) {
                floorCell.children.forEach((cellChild) => {
                    if (cellChild && cellChild.name === "explosionHighlight") {
                        if (cellChild) {
                            tl.to(cellChild, {
                                alpha: 0,
                                duration
                            }, 0);
                            tl.add(() => {
                                if (cellChild.parent) {
                                    cellChild.parent.removeChild(cellChild);
                                    cellChild.destroy();
                                }
                            });
                        }
                    }
                })
            }
        }

        return tl;
    }

    // reposition entire "this" from here to match the wall positions and others
    public rePositionEntireThisOnGridResize(duration: number, gridSize: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            [xOffset, yOffset]: number[] = this.getReelPositions(gridSize);

        tl.to(this, {
            duration,
            x: xOffset,
            y: yOffset
        }, "<");

        return tl;
    }

    // #343
    public onGameModeChange(nextMode: string): void {
        const textureHelper: string = this.getTextureName(nextMode);

        this._floorsContainer.alpha = (nextMode.includes("NORMAL") ? 0.8 : 0.95);

        this._floors.forEach((floors: PIXI.Sprite[]) => {
            floors.forEach((floor: PIXI.Sprite) => {
                floor.texture = Game.assetLoader.getTextures(textureHelper)[0];
            });
        });
    }

    public drawTrack(sym: Symbol, symReelId: number, currPos: IFeatureData, prevPos: IFeatureData, nxtPos: IFeatureData, isFirstPosition: boolean = false): void {
        const trackPos = this.determineTrackPosition(currPos, prevPos, nxtPos);
        let trackIcon = new PIXI.Sprite(Game.assetLoader.getTextures("track/track_ver")[0]);

        if (isFirstPosition) {
            switch (trackPos) {
                case 'right':
                    trackIcon = new PIXI.Sprite(Game.assetLoader.getTextures("track/track_verR")[0]);
                    break;
                case 'left':
                    trackIcon = new PIXI.Sprite(Game.assetLoader.getTextures("track/track_verL")[0]);
                    break;
                case 'top':
                    trackIcon = new PIXI.Sprite(Game.assetLoader.getTextures("track/track_horT")[0]);
                    break;
                case 'bottom':
                    trackIcon = new PIXI.Sprite(Game.assetLoader.getTextures("track/track_horB")[0]);
                    break;
                default:
                    break;
            }
        } else {
            switch (trackPos) {
                case 'right':
                    trackIcon = new PIXI.Sprite(Game.assetLoader.getTextures("track/track_ver")[0]);
                    break;
                case 'left':
                    trackIcon = new PIXI.Sprite(Game.assetLoader.getTextures("track/track_ver")[0]);
                    break;
                case 'top':
                    trackIcon = new PIXI.Sprite(Game.assetLoader.getTextures("track/track_hor")[0]);
                    break;
                case 'bottom':
                    trackIcon = new PIXI.Sprite(Game.assetLoader.getTextures("track/track_hor")[0]);
                    break;
                case 'top-right':
                    trackIcon = new PIXI.Sprite(Game.assetLoader.getTextures("track/track_TR")[0]);
                    break;
                case 'bottom-right':
                    trackIcon = new PIXI.Sprite(Game.assetLoader.getTextures("track/track_BR")[0]);
                    break;
                case 'bottom-left':
                    trackIcon = new PIXI.Sprite(Game.assetLoader.getTextures("track/track_BL")[0]);
                    break;
                case 'top-left':
                    trackIcon = new PIXI.Sprite(Game.assetLoader.getTextures("track/track_TL")[0]);
                    break;
                default:
                    break;
            }
        }

        trackIcon.name = "trackIcon" + symReelId + "_" + sym.reelPosition;
        trackIcon.blendMode = PIXI.BLEND_MODES.MULTIPLY;
        this._floors[symReelId][sym.reelPosition].addChild(trackIcon);
    }

    public removeTrack(duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        for (let i = 0; i < Game.ACTIVE_REELS_NUM; i++) {
            for (let j = 0; j < Game.ACTIVE_ROWS_NUM; j++) {
                if (!this._floors[i][j]) return tl;

                const floors: PIXI.DisplayObject[] = this._floors[i][j].children;

                tl.to(floors, {
                    duration,
                    alpha: 0
                }, 0);

                tl.add(() => {
                    if (floors && floors.length > 0) {
                        this._floors[i][j].removeChildren();
                    }
                });
            }
        }


        return tl;
    }

    public fadeTrack(duration: number, alpha: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        for (let i = 0; i < Game.ACTIVE_REELS_NUM; i++) {
            for (let j = 0; j < Game.ACTIVE_ROWS_NUM; j++) {
                this._floors[i][j].children.forEach((floorItem: PIXI.DisplayObject) => {
                    if (!floorItem) return;

                    if (floorItem?.name?.includes("trackIcon")) {
                        tl.to(floorItem, {
                            duration,
                            alpha
                        }, 0);
                    } else {
                        tl.to(floorItem, {
                            duration,
                            alpha: 0.7
                        }, 0);
                    }
                })
            }
        }

        return tl;
    }

    public showBloodStain(reelId: number, rowId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        const textureStr: string = "floor_mark/floor_mark_blood_" + String.fromCharCode(65 + MathHelper.randomIntInRange(0, 2));
        const bloodSpot: PIXI.Sprite = new PIXI.Sprite(Game.assetLoader.getTextures(textureStr)[0]);
        bloodSpot.blendMode = BLEND_MODES.MULTIPLY;
        bloodSpot.visible = true;
        bloodSpot.alpha = 0;
        bloodSpot.name = "bloodStain" + reelId + "_" + rowId;

        this._floors[reelId][rowId].addChild(bloodSpot);

        tl.to(bloodSpot, {
            duration: 0.2,
            alpha: 1
        });

        return tl;
    }

    public showExplosionMark(reelId: number, rowId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        const textureStr: string = "floor_mark/floor_mark_explos_" + String.fromCharCode(65 + MathHelper.randomIntInRange(0, 2));
        const explodeSpot: PIXI.Sprite = new PIXI.Sprite(Game.assetLoader.getTextures(textureStr)[0]);
        explodeSpot.blendMode = BLEND_MODES.MULTIPLY;
        explodeSpot.visible = true;
        explodeSpot.alpha = 0;
        explodeSpot.name = "explodeSpot" + reelId + "_" + rowId;

        this._floors[reelId][rowId].addChild(explodeSpot);

        tl.to(explodeSpot, {
            duration: 0.2,
            alpha: 1
        });

        return tl;
    }

    public playHatTossAnimation(reelId: number, rowId: number, monsterSkin: string, monsterLife: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(() => {
            if (monsterLife === 1) {
                this._hatHurtAnimation.alpha = 1;
                this._hatHurtAnimation.skeleton.setSkinByName(monsterSkin);
                this._floors[reelId][rowId].addChild(this._hatHurtAnimation);
                this._hatHurtAnimation.state.setAnimation(0, "drop_hat", false);
            } else {
                this._hatAnimation.alpha = 1;
                this._hatAnimation.skeleton.setSkinByName(monsterSkin);
                this._floors[reelId][rowId].addChild(this._hatAnimation);
                this._hatAnimation.state.setAnimation(0, "drop_hat", false);
            }
        });
        return tl;
    }

    public moveHatToTopOnFloor(reelId: number, rowId: number, monsterLife: number): void {
        if (monsterLife === 1) {
            this._floors[reelId][rowId].addChild(this._hatHurtAnimation);
        } else {
            this._floors[reelId][rowId].addChild(this._hatAnimation);
        }
    }

    public hideHat(monsterLife: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(() => {
            if (monsterLife === 1) {
                this._hatHurtAnimation.alpha = 0;
                this._hatHurtAnimation.parent.removeChild(this._hatHurtAnimation);
            } else {
                this._hatAnimation.alpha = 0;
                this._hatAnimation.parent.removeChild(this._hatAnimation);
            }
        });

        return tl;
    }

    private determineTrackPosition(currPos: IFeatureData, prvPos: IFeatureData, nxtPos: IFeatureData): string {
        const {reelId: currX, rowId: currY} = currPos;
        const {reelId: prvX, rowId: prvY} = prvPos;
        const {reelId: nxtX, rowId: nxtY} = nxtPos;
        let direction: string = '';

        if (prvX === -1 || prvY === -1) { // start pos
            if (nxtX > currX && nxtY === currY) {
                direction = 'right';
            } else if (nxtX < currX && nxtY === currY) {
                direction = 'left';
            } else if (nxtX === currX && nxtY > currY) {
                direction = 'bottom';
            } else if (nxtX === currX && nxtY < currY) {
                direction = 'top';
            }
        } else if (nxtX === -1 || nxtY === -1) { // end pos
            if (currX > prvX && currY === prvY) {
                direction = 'right';
            } else if (currX < prvX && currY === prvY) {
                direction = 'left';
            } else if (currX === prvX && currY > prvY) {
                direction = 'top';
            } else if (currX === prvX && currY < prvY) {
                direction = 'bottom';
            }
        } else { // inbetween pos
            // starting from top-right moving clockwise
            if ((currX > prvX && currY < nxtY) || (currY < prvY && currX > nxtX)) {
                direction = 'top-right';
            } else if ((currY > prvY && currX > nxtX) || (currX > prvX && currY > nxtY)) {
                direction = 'bottom-right';
            } else if ((currX < prvX && currY > nxtY) || (currY > prvY && currX < nxtX)) {
                direction = 'bottom-left';
            } else if ((currY < prvY && currX < nxtX) || (currX < prvX && currY < nxtY)) {
                direction = 'top-left';
            } else if (currX === nxtX) {
                direction = 'top';
            } else if (currY === nxtY) {
                direction = 'right';
            }
        }

        return direction;
    }

    private init(): void {
        const [xOffset, yOffset]: number[] = this.getReelPositions(Game.gridSize),
            textureName: string = this.getTextureName(Game.model.data.mode);
        this.x = xOffset;
        this.y = yOffset;

        this._floorsContainer = new PIXI.Container();
        this._floorsContainer.alpha = (Game.model.data.mode.includes("NORMAL") ? 0.8 : 0.95);

        this._floors = [];

        for (let reel: number = 0; reel < Game.ACTIVE_REELS_NUM; reel++) {
            this._floors[reel] = [];
            for (let row: number = 0; row < Game.ACTIVE_ROWS_NUM; row++) {
                const floor: PIXI.Sprite = new PIXI.Sprite(Game.assetLoader.getTextures(textureName)[0]);
                floor.position.set((reel * Game.DEFAULT_SYMBOL_WIDTH), (row * Game.DEFAULT_SYMBOL_HEIGHT));

                this._floors[reel].push(floor);
                this._floorsContainer.addChild(floor);
            }
        }
        this._floorsContainer.position.set(360, 385);
        this._floorsContainer.pivot.set(330, 300);

        this.createSymbolFloorMask();

        this._hatAnimation = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("hat"));
        this._hatAnimation.position.set(55, 22);
        this._hatAnimation.alpha = 0;

        this._hatHurtAnimation = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("hatHurt"));
        this._hatHurtAnimation.position.set(55, 22);
        this._hatHurtAnimation.alpha = 0;

        this.addChild(this._floorsContainer);
    }

    private createSymbolFloorMask(): void {
        if (this.getChildByName("FLOOR_MASK")) {
            this.removeChild(this.getChildByName("FLOOR_MASK"));
        }

        const reelMask: PIXI.Graphics = new PIXI.Graphics();
        reelMask.name = "FLOOR_MASK";

        this._floorsContainer.mask = reelMask;

        const leftMask: PIXI.Graphics = new PIXI.Graphics();
        const rightMask: PIXI.Graphics = new PIXI.Graphics();
        const topMask: PIXI.Graphics = new PIXI.Graphics();
        const bottomMask: PIXI.Graphics = new PIXI.Graphics();
        topMask.beginFill(0x000000);
        topMask.moveTo(Game.DEFAULT_SYMBOL_WIDTH, 0)
            .lineTo(Game.DEFAULT_SYMBOL_WIDTH, -Game.DEFAULT_SYMBOL_HEIGHT)
            .lineTo(((Game.DEFAULT_REELS_NUM - 1) * Game.DEFAULT_SYMBOL_WIDTH), -Game.DEFAULT_SYMBOL_HEIGHT)
            .lineTo(((Game.DEFAULT_REELS_NUM - 1) * Game.DEFAULT_SYMBOL_WIDTH), 0)
            .lineTo((Game.DEFAULT_SYMBOL_WIDTH), 0)
            .lineTo(Game.DEFAULT_SYMBOL_WIDTH, -Game.DEFAULT_SYMBOL_HEIGHT);
        topMask.endFill();

        bottomMask.beginFill(0x000000);
        bottomMask.moveTo(Game.DEFAULT_SYMBOL_WIDTH, (Game.DEFAULT_ROWS_NUM - 2) * Game.DEFAULT_SYMBOL_HEIGHT)
            .lineTo(((Game.DEFAULT_REELS_NUM - 1) * Game.DEFAULT_SYMBOL_WIDTH), (Game.DEFAULT_ROWS_NUM - 2) * Game.DEFAULT_SYMBOL_HEIGHT)
            .lineTo(((Game.DEFAULT_REELS_NUM - 1) * Game.DEFAULT_SYMBOL_WIDTH), (Game.DEFAULT_ROWS_NUM - 1) * Game.DEFAULT_SYMBOL_HEIGHT)
            .lineTo((Game.DEFAULT_SYMBOL_WIDTH), (Game.DEFAULT_ROWS_NUM - 1) * Game.DEFAULT_SYMBOL_HEIGHT)
            .lineTo(Game.DEFAULT_SYMBOL_WIDTH, (Game.DEFAULT_ROWS_NUM - 1) * Game.DEFAULT_SYMBOL_HEIGHT);
        bottomMask.endFill();

        leftMask.beginFill(0x000000);
        leftMask.lineTo(Game.DEFAULT_SYMBOL_WIDTH, 0)
            .lineTo(Game.DEFAULT_SYMBOL_WIDTH, ((Game.DEFAULT_ROWS_NUM - 2) * Game.DEFAULT_SYMBOL_HEIGHT))
            .lineTo(0, ((Game.DEFAULT_ROWS_NUM - 2) * Game.DEFAULT_SYMBOL_HEIGHT))
            .lineTo(0, 0)
            .lineTo(Game.DEFAULT_SYMBOL_WIDTH, 0);
        leftMask.endFill();

        rightMask.beginFill(0x000000);
        rightMask.moveTo((Game.DEFAULT_REELS_NUM - 1) * Game.DEFAULT_SYMBOL_WIDTH, 0)
            .lineTo((Game.DEFAULT_REELS_NUM) * Game.DEFAULT_SYMBOL_WIDTH, 0)
            .lineTo((Game.DEFAULT_REELS_NUM) * Game.DEFAULT_SYMBOL_WIDTH, ((Game.DEFAULT_ROWS_NUM - 2) * Game.DEFAULT_SYMBOL_HEIGHT))
            .lineTo((Game.DEFAULT_REELS_NUM - 1) * Game.DEFAULT_SYMBOL_WIDTH, ((Game.DEFAULT_ROWS_NUM - 2) * Game.DEFAULT_SYMBOL_HEIGHT))
            .lineTo((Game.DEFAULT_REELS_NUM - 1) * Game.DEFAULT_SYMBOL_WIDTH, 0);
        rightMask.endFill();

        reelMask.addChild(leftMask, topMask, rightMask, bottomMask);
        leftMask.x = topMask.x = rightMask.x = bottomMask.x = -110;

        reelMask.position.set(360, 385);
        reelMask.pivot.set(220, 200);
        reelMask.beginFill(0xffffff);
        reelMask.drawRect(0, 0, 440, 400);

        this._maskItem = reelMask;
        this.addChild(reelMask);
    }

    private increaseRows(startIdxForRows: number, extraRows: number, addRowsInBottom: boolean, id: number, xOffsetHelper: number, xScaleForNewFloor: number, yScaleForNewFloor: number, prevWidth: number, prevHeight: number, textureHelper: string): void {
        if (addRowsInBottom) {
            for (let row: number = startIdxForRows; row < extraRows + Game.ACTIVE_ROWS_NUM; row++) {
                const tempFloor: PIXI.Sprite = new PIXI.Sprite(Game.assetLoader.getTextures(textureHelper)[0]);
                tempFloor.scale.set(xScaleForNewFloor, yScaleForNewFloor);
                tempFloor.position.set(((id + xOffsetHelper) * prevWidth), (row * prevHeight) - 10);
                tempFloor.alpha = 0;

                this._floors[id].push(tempFloor);
                this._floorsContainer.addChild(tempFloor);
            }
        } else if (extraRows) {
            for (let row: number = 0; row < extraRows; row++) {
                const tempFloor: PIXI.Sprite = new PIXI.Sprite(Game.assetLoader.getTextures(textureHelper)[0]);
                tempFloor.scale.set(xScaleForNewFloor, yScaleForNewFloor);
                tempFloor.position.set(((id + xOffsetHelper) * prevWidth), 0);
                tempFloor.alpha = 0;

                // this.playExplodeAnimations(tempFloor);

                this._floors[id].unshift(tempFloor);
                this._floorsContainer.addChild(tempFloor);
            }
            for (let row: number = 0; row < extraRows; row++) {
                const tempFloor: PIXI.Sprite = this._floors[id][row];

                tempFloor.y = ((row - extraRows) * prevHeight);
            }
        }
    }

    private getReelPositions(gridName: string): number[] {
        switch (gridName) {
            case "8by6":
                return [-3, -5];
            case "6by8":
                return [-5, -25]
            case "8by8":
                return [-2, -24];
            case "9by8":
                return [2, -25];
            case "8by9":
                return [-2, -30]
            case "9by9":
                return [-3, -30];
            default:
                return [0, 0];
        }
    }

    private getTextureName(mode: string): string {
        let textureHelper: string = "floor";

        // #343
        if (!mode.includes("NORMAL")) {
            if (Game.model.data.mayhemSpin) {
                textureHelper += "_Mayhem";
            } else if (mode.includes("FREESPIN")) {
                textureHelper += "_FS";
            }
        }

        this._textureName = textureHelper;

        return textureHelper;
    }
}